'use client'

import Link from 'next/link'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { TOTAL_LESSONS } from '../_lib/curriculum'
import { learnerLoss } from '../_lib/progress'
import { useHydrated, useProgress, useSyncInfo } from '../_lib/progress-client'
import { setTheme, useTheme } from '../_lib/theme-client'
import { GradientMark, IconCode, IconMoon, IconSun, IconSync } from './icons'
import SyncPanel from './SyncPanel'

/** Scroll-linked progress hairline under the header (lesson pages only). */
function ReadingProgress() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let raf = 0
    const update = () => {
      raf = 0
      const el = ref.current
      if (!el) return
      const max = document.documentElement.scrollHeight - window.innerHeight
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0
      el.style.transform = `scaleX(${p})`
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])
  return <div ref={ref} className="gd-header-progress" style={{ width: '100%', transform: 'scaleX(0)' }} />
}

function LossMeter() {
  const state = useProgress()
  const hydrated = useHydrated()
  const done = Object.keys(state.done).length
  const loss = learnerLoss(done, TOTAL_LESSONS)
  return (
    <span
      className="gd-loss-meter"
      title={`Your training loss. Everyone starts at ln(10) ≈ 2.303 — the score of pure guessing. Each lesson you finish pushes it toward zero. (${done}/${TOTAL_LESSONS} lessons)`}
    >
      <span className="hidden sm:inline">loss</span>
      <b suppressHydrationWarning>{hydrated ? loss.toFixed(3) : '2.303'}</b>
      <span aria-hidden style={{ color: 'var(--gd-green)' }}>
        ↓
      </span>
    </span>
  )
}

export default function GdHeader({ center, showProgress = false }: { center?: ReactNode; showProgress?: boolean }) {
  const theme = useTheme()
  const sync = useSyncInfo()
  const [syncOpen, setSyncOpen] = useState(false)

  return (
    <>
      <header className="gd-header">
        <Link href="/" className="flex shrink-0 items-center gap-2 no-underline" aria-label="Gradient — course home">
          <GradientMark />
          <span className="gd-serif text-[23px] leading-none tracking-[-0.02em]">Gradient</span>
        </Link>

        <div className="min-w-0 flex-1">{center}</div>

        <nav className="flex shrink-0 items-center gap-1.5" aria-label="Course tools">
          <LossMeter />
          <Link href="/lab" className="gd-icon-btn" title="The Lab — free-form code playground" aria-label="Open the Lab">
            <IconCode />
          </Link>
          <button
            type="button"
            className="gd-icon-btn relative"
            onClick={() => setSyncOpen(true)}
            title="Sync progress across devices"
            aria-label="Sync progress across devices"
          >
            <IconSync />
            <span
              className="gd-status-dot absolute right-[7px] top-[7px] !h-[7px] !w-[7px]"
              data-status={sync.key ? sync.status : 'local'}
              suppressHydrationWarning
            />
          </button>
          <button
            type="button"
            className="gd-icon-btn"
            onClick={() => setTheme(theme === 'night' ? 'paper' : 'night')}
            aria-label={theme === 'night' ? 'Switch to paper (light) theme' : 'Switch to night (dark) theme'}
            title="Toggle theme"
            suppressHydrationWarning
          >
            {theme === 'night' ? <IconSun /> : <IconMoon />}
          </button>
        </nav>
        {showProgress && <ReadingProgress />}
      </header>
      {syncOpen && <SyncPanel onClose={() => setSyncOpen(false)} />}
    </>
  )
}
